import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  RefreshCw,
  Shield,
  Clock,
  CheckCircle,
  XCircle,
  HelpCircle,
  Settings,
  Activity,
  FileText,
  AlertTriangle
} from 'lucide-react';
import { adminApi } from '@/lib/api';

// ═══════════════════════════════════════════════════════════════════════════
// Types
// ═══════════════════════════════════════════════════════════════════════════

interface DefaultPolicy {
  category: string;
  name: string;
  description: string;
  settings: Record<string, unknown>;
}

interface PolicyStats {
  totalPolicies: number;
  byResourceType: Record<string, number>;
  byAction: Record<string, number>;
  byEffect: Record<string, number>;
  recentlyCreated: number;
  expiringPolicies: number;
}

interface RequestStats {
  total: number;
  pending: number;
  approved: number;
  denied: number;
  expired: number;
  byType: Record<string, number>;
}

interface PolicyMetrics {
  totalEvaluations: number;
  allowDecisions: number;
  denyDecisions: number;
  askDecisions: number;
  ownerDecisions: number;
  explicitPolicyDecisions: number;
  inheritedPolicyDecisions: number;
  defaultDenyDecisions: number;
  errorDecisions: number;
  cacheHits: number;
  cacheMisses: number;
  totalLatencyMs: number;
  avgLatencyMs: number;
  resetTime: string;
  evaluationsByResourceType: Record<string, number>;
  evaluationsByAction: Record<string, number>;
}

interface PolicyConfig {
  verboseLogging: boolean;
  metricsEnabled: boolean;
  cacheTtlMs: number;
  maxCacheSize: number;
}

interface PolicyOverview {
  config: PolicyConfig;
  defaults: DefaultPolicy[];
  stats: PolicyStats;
  requestStats: RequestStats;
  metrics: PolicyMetrics;
  connectorSchemas: {
    connectorId: string;
    displayName: string;
    version: string;
    scopeCount: number;
    toolCount: number;
    defaultScopes: string[];
  }[];
}

// ═══════════════════════════════════════════════════════════════════════════
// Component
// ═══════════════════════════════════════════════════════════════════════════

export function PoliciesPage() {
  const [overview, setOverview] = useState<PolicyOverview | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [resettingMetrics, setResettingMetrics] = useState(false);

  const fetchOverview = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await adminApi.getPolicyOverview();
      setOverview(data);
    } catch (err: any) {
      setError(err.response?.data?.error || err.message || 'Failed to fetch policy overview');
    } finally {
      setLoading(false);
    }
  };

  const handleResetMetrics = async () => {
    try {
      setResettingMetrics(true);
      await adminApi.resetPolicyMetrics();
      await fetchOverview();
    } catch (err: any) {
      setError(err.response?.data?.error || err.message || 'Failed to reset metrics');
    } finally {
      setResettingMetrics(false);
    }
  };

  useEffect(() => {
    fetchOverview();
  }, []);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <RefreshCw className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-6">
        <Card className="border-destructive">
          <CardContent className="pt-6">
            <div className="flex items-center gap-2 text-destructive">
              <AlertTriangle className="h-5 w-5" />
              <span>{error}</span>
            </div>
            <Button onClick={fetchOverview} className="mt-4" variant="outline">
              Retry
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (!overview) return null;

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">Permission Policies</h1>
          <p className="text-muted-foreground">
            Unified permission system configuration and defaults
          </p>
        </div>
        <Button onClick={fetchOverview} variant="outline" size="sm">
          <RefreshCw className="h-4 w-4 mr-2" />
          Refresh
        </Button>
      </div>

      {/* Configuration Status */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Settings className="h-5 w-5" />
            System Configuration
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
            <ConfigBadge
              label="Verbose Logging"
              enabled={overview.config.verboseLogging}
            />
            <ConfigBadge
              label="Metrics Enabled"
              enabled={overview.config.metricsEnabled}
            />
            <div className="text-sm">
              <span className="text-muted-foreground">Cache TTL:</span>
              <span className="ml-2 font-medium">{Math.round(overview.config.cacheTtlMs / 1000)}s</span>
            </div>
            <div className="text-sm">
              <span className="text-muted-foreground">Max Cache:</span>
              <span className="ml-2 font-medium">{overview.config.maxCacheSize.toLocaleString()}</span>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Tabs for different views */}
      <Tabs defaultValue="defaults" className="space-y-4">
        <TabsList>
          <TabsTrigger value="defaults">Default Policies</TabsTrigger>
          <TabsTrigger value="stats">Statistics</TabsTrigger>
          <TabsTrigger value="metrics">Engine Metrics</TabsTrigger>
          <TabsTrigger value="connectors">Connectors</TabsTrigger>
        </TabsList>

        {/* Default Policies Tab */}
        <TabsContent value="defaults" className="space-y-4">
          {groupPoliciesByCategory(overview.defaults).map(([category, policies]) => (
            <Card key={category}>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Shield className="h-5 w-5" />
                  {category} Defaults
                </CardTitle>
              </CardHeader>
              <CardContent>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Name</TableHead>
                      <TableHead>Description</TableHead>
                      <TableHead>Settings</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {policies.map((policy) => (
                      <TableRow key={policy.name}>
                        <TableCell className="font-medium">{policy.name}</TableCell>
                        <TableCell className="text-muted-foreground">{policy.description}</TableCell>
                        <TableCell>
                          <pre className="text-xs bg-muted p-2 rounded overflow-x-auto max-w-md">
                            {JSON.stringify(policy.settings, null, 2)}
                          </pre>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          ))}
        </TabsContent>

        {/* Statistics Tab */}
        <TabsContent value="stats" className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Policy Stats */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <FileText className="h-5 w-5" />
                  Policy Statistics
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <StatItem label="Total Policies" value={overview.stats.totalPolicies} />
                  <StatItem label="Created (24h)" value={overview.stats.recentlyCreated} />
                  <StatItem label="Expiring (7d)" value={overview.stats.expiringPolicies} warning />
                </div>

                <div>
                  <h4 className="text-sm font-medium mb-2">By Resource Type</h4>
                  <div className="flex flex-wrap gap-2">
                    {Object.entries(overview.stats.byResourceType).map(([type, count]) => (
                      <Badge key={type} variant="outline">
                        {type}: {count}
                      </Badge>
                    ))}
                  </div>
                </div>

                <div>
                  <h4 className="text-sm font-medium mb-2">By Effect</h4>
                  <div className="flex flex-wrap gap-2">
                    {Object.entries(overview.stats.byEffect).map(([effect, count]) => (
                      <Badge
                        key={effect}
                        variant={effect === 'allow' ? 'default' : effect === 'deny' ? 'destructive' : 'secondary'}
                      >
                        {effect}: {count}
                      </Badge>
                    ))}
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Request Stats */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Clock className="h-5 w-5" />
                  Request Statistics
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <StatItem label="Total Requests" value={overview.requestStats.total} />
                  <StatItem label="Pending" value={overview.requestStats.pending} warning={overview.requestStats.pending > 0} />
                  <StatItem label="Approved" value={overview.requestStats.approved} />
                  <StatItem label="Denied" value={overview.requestStats.denied} />
                  <StatItem label="Expired" value={overview.requestStats.expired} />
                </div>

                {Object.keys(overview.requestStats.byType).length > 0 && (
                  <div>
                    <h4 className="text-sm font-medium mb-2">By Type</h4>
                    <div className="flex flex-wrap gap-2">
                      {Object.entries(overview.requestStats.byType).map(([type, count]) => (
                        <Badge key={type} variant="outline">
                          {type}: {count}
                        </Badge>
                      ))}
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* Engine Metrics Tab */}
        <TabsContent value="metrics" className="space-y-4">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <div>
                <CardTitle className="flex items-center gap-2">
                  <Activity className="h-5 w-5" />
                  PolicyEngine Metrics
                </CardTitle>
                <CardDescription>
                  Since: {new Date(overview.metrics.resetTime).toLocaleString()}
                </CardDescription>
              </div>
              <Button
                onClick={handleResetMetrics}
                variant="outline"
                size="sm"
                disabled={resettingMetrics}
              >
                {resettingMetrics ? (
                  <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                ) : (
                  <RefreshCw className="h-4 w-4 mr-2" />
                )}
                Reset Metrics
              </Button>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Overview Stats */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <MetricCard
                  label="Total Evaluations"
                  value={overview.metrics.totalEvaluations}
                  icon={<Activity className="h-4 w-4" />}
                />
                <MetricCard
                  label="Avg Latency"
                  value={`${overview.metrics.avgLatencyMs.toFixed(2)}ms`}
                  icon={<Clock className="h-4 w-4" />}
                />
                <MetricCard
                  label="Cache Hit Rate"
                  value={formatCacheHitRate(overview.metrics)}
                  icon={<CheckCircle className="h-4 w-4" />}
                />
                <MetricCard
                  label="Error Rate"
                  value={formatErrorRate(overview.metrics)}
                  icon={<AlertTriangle className="h-4 w-4" />}
                  warning={overview.metrics.errorDecisions > 0}
                />
              </div>

              {/* Decision Breakdown */}
              <div>
                <h4 className="text-sm font-medium mb-3">Decision Breakdown</h4>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <DecisionStat
                    label="Allow"
                    value={overview.metrics.allowDecisions}
                    total={overview.metrics.totalEvaluations}
                    icon={<CheckCircle className="h-4 w-4 text-green-500" />}
                  />
                  <DecisionStat
                    label="Deny"
                    value={overview.metrics.denyDecisions}
                    total={overview.metrics.totalEvaluations}
                    icon={<XCircle className="h-4 w-4 text-red-500" />}
                  />
                  <DecisionStat
                    label="Ask"
                    value={overview.metrics.askDecisions}
                    total={overview.metrics.totalEvaluations}
                    icon={<HelpCircle className="h-4 w-4 text-yellow-500" />}
                  />
                  <DecisionStat
                    label="Owner"
                    value={overview.metrics.ownerDecisions}
                    total={overview.metrics.totalEvaluations}
                    icon={<Shield className="h-4 w-4 text-blue-500" />}
                  />
                </div>
              </div>

              {/* Decision Sources */}
              <div>
                <h4 className="text-sm font-medium mb-3">Decision Sources</h4>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <StatItem label="Explicit Policy" value={overview.metrics.explicitPolicyDecisions} />
                  <StatItem label="Inherited Policy" value={overview.metrics.inheritedPolicyDecisions} />
                  <StatItem label="Owner Access" value={overview.metrics.ownerDecisions} />
                  <StatItem label="Default Deny" value={overview.metrics.defaultDenyDecisions} />
                </div>
              </div>

              {/* By Resource Type */}
              {Object.keys(overview.metrics.evaluationsByResourceType).length > 0 && (
                <div>
                  <h4 className="text-sm font-medium mb-2">Evaluations by Resource Type</h4>
                  <div className="flex flex-wrap gap-2">
                    {Object.entries(overview.metrics.evaluationsByResourceType)
                      .sort((a, b) => b[1] - a[1])
                      .map(([type, count]) => (
                        <Badge key={type} variant="outline">
                          {type}: {count.toLocaleString()}
                        </Badge>
                      ))}
                  </div>
                </div>
              )}

              {/* By Action */}
              {Object.keys(overview.metrics.evaluationsByAction).length > 0 && (
                <div>
                  <h4 className="text-sm font-medium mb-2">Evaluations by Action</h4>
                  <div className="flex flex-wrap gap-2">
                    {Object.entries(overview.metrics.evaluationsByAction)
                      .sort((a, b) => b[1] - a[1])
                      .map(([action, count]) => (
                        <Badge key={action} variant="outline">
                          {action}: {count.toLocaleString()}
                        </Badge>
                      ))}
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Connectors Tab */}
        <TabsContent value="connectors" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>Registered Connector Schemas</CardTitle>
              <CardDescription>
                Connectors that have registered their permission schemas
              </CardDescription>
            </CardHeader>
            <CardContent>
              {overview.connectorSchemas.length === 0 ? (
                <p className="text-muted-foreground">No connector schemas registered</p>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Connector</TableHead>
                      <TableHead>Version</TableHead>
                      <TableHead>Scopes</TableHead>
                      <TableHead>Tools</TableHead>
                      <TableHead>Default Scopes</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {overview.connectorSchemas.map((schema) => (
                      <TableRow key={schema.connectorId}>
                        <TableCell className="font-medium">{schema.displayName}</TableCell>
                        <TableCell>{schema.version}</TableCell>
                        <TableCell>{schema.scopeCount}</TableCell>
                        <TableCell>{schema.toolCount}</TableCell>
                        <TableCell>
                          <div className="flex flex-wrap gap-1">
                            {schema.defaultScopes.map((scope) => (
                              <Badge key={scope} variant="secondary" className="text-xs">
                                {scope}
                              </Badge>
                            ))}
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
// Helper Components
// ═══════════════════════════════════════════════════════════════════════════

function ConfigBadge({ label, enabled }: { label: string; enabled: boolean }) {
  return (
    <div className="flex items-center gap-2">
      <span className={`h-2 w-2 rounded-full ${enabled ? 'bg-green-500' : 'bg-gray-400'}`} />
      <span className="text-sm">{label}</span>
    </div>
  );
}

function StatItem({ label, value, warning = false }: { label: string; value: number; warning?: boolean }) {
  return (
    <div>
      <p className="text-sm text-muted-foreground">{label}</p>
      <p className={`text-2xl font-bold ${warning && value > 0 ? 'text-yellow-500' : ''}`}>
        {value.toLocaleString()}
      </p>
    </div>
  );
}

function MetricCard({
  label,
  value,
  icon,
  warning = false
}: {
  label: string;
  value: string | number;
  icon: React.ReactNode;
  warning?: boolean;
}) {
  return (
    <div className={`p-4 rounded-lg border ${warning ? 'border-yellow-500/50 bg-yellow-500/10' : 'bg-muted/50'}`}>
      <div className="flex items-center gap-2 text-muted-foreground mb-1">
        {icon}
        <span className="text-sm">{label}</span>
      </div>
      <p className="text-xl font-bold">{typeof value === 'number' ? value.toLocaleString() : value}</p>
    </div>
  );
}

function DecisionStat({
  label,
  value,
  total,
  icon
}: {
  label: string;
  value: number;
  total: number;
  icon: React.ReactNode;
}) {
  const percentage = total > 0 ? ((value / total) * 100).toFixed(1) : '0';
  return (
    <div className="p-4 rounded-lg border bg-muted/50">
      <div className="flex items-center gap-2 mb-1">
        {icon}
        <span className="text-sm font-medium">{label}</span>
      </div>
      <p className="text-xl font-bold">{value.toLocaleString()}</p>
      <p className="text-xs text-muted-foreground">{percentage}%</p>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
// Helper Functions
// ═══════════════════════════════════════════════════════════════════════════

function groupPoliciesByCategory(policies: DefaultPolicy[]): [string, DefaultPolicy[]][] {
  const groups: Record<string, DefaultPolicy[]> = {};
  for (const policy of policies) {
    if (!groups[policy.category]) {
      groups[policy.category] = [];
    }
    groups[policy.category]!.push(policy);
  }
  return Object.entries(groups);
}

function formatCacheHitRate(metrics: PolicyMetrics): string {
  const total = metrics.cacheHits + metrics.cacheMisses;
  if (total === 0) return '0%';
  return `${((metrics.cacheHits / total) * 100).toFixed(1)}%`;
}

function formatErrorRate(metrics: PolicyMetrics): string {
  if (metrics.totalEvaluations === 0) return '0%';
  return `${((metrics.errorDecisions / metrics.totalEvaluations) * 100).toFixed(2)}%`;
}

export default PoliciesPage;
